/* eslint-disable react/prop-types */
import { TrashIcon } from '@heroicons/react/outline';
import { KeyboardEvent, useState, useEffect, ChangeEvent } from 'react';

interface MultipleTextItemInputProps {
  // Initial values to populate the input
  initialValues?: string[];
  // Callback function that returns the current items whenever they change
  onChangeItems?: (items: string[]) => void;
  // Optional placeholder text for the input field
  placeholder?: string;
  // Optional title for the component
  title?: string;
  // Maximum number of items allowed (optional)
  maxItems?: number;
}

const MultipleTextItemInput: React.FC<MultipleTextItemInputProps> = ({
  initialValues = [],
  onChangeItems,
  placeholder = 'Add new item...',
  title = 'Manage Items',
  maxItems,
}) => {
  const [items, setItems] = useState<string[]>(initialValues);
  const [inputValue, setInputValue] = useState<string>('');
  const isMaxItemsReached = maxItems !== undefined && items.length >= maxItems;

  useEffect(() => {
    if (onChangeItems) {
      onChangeItems(items);
    }
  }, [items, onChangeItems]);

  const addItem = (): void => {
    if (inputValue.trim() !== '' && !isMaxItemsReached) {
      const newItems = [...items, inputValue.trim()];
      setItems(newItems);
      setInputValue('');
    }
  };

  const removeItem = (index: number): void => {
    const newItems = [...items];
    newItems.splice(index, 1);
    setItems(newItems);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addItem();
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setInputValue(e.target.value);
  };

  return (
    <div className="w-full max-w-md my-2 p-4 bg-white rounded shadow">
      <h2 className="text-md mb-4">{title}</h2>

      <div className="flex mb-4">
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={isMaxItemsReached}
          className="flex-grow px-3 py-2 border rounded-l focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
        />
        <button
          onClick={addItem}
          disabled={inputValue.trim() === '' || isMaxItemsReached}
          className="bg-blue-500 text-white px-4 py-2 rounded-r hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          Add
        </button>
      </div>

      {isMaxItemsReached && (
        <p className="text-amber-500 text-sm mb-2">
          Maximum number of items reached ({maxItems})
        </p>
      )}

      {items.length > 0 ? (
        <ul className="flex flex-wrap justify-start items-center gap-2">
          {items.map((item, index) => (
            <li
              key={index}
              className="flex items-center justify-between bg-gray-100 px-2 py-1 rounded"
            >
              <span className="mr-2">{item}</span>
              <button
                onClick={() => removeItem(index)}
                className="text-red-500 hover:text-red-700 focus:outline-none"
                aria-label={`Remove ${item}`}
              >
                <TrashIcon style={{ width: '18px' }} />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-gray-500 italic">No items added yet</p>
      )}
    </div>
  );
};

export default MultipleTextItemInput;
